import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button, List, ListItemText, Popover, Typography } from "@mui/material";
import type { MouseEvent } from "react";
import { useState } from "react";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem";
import type { SmartViewArtifactSource } from "../../../../../api/types/smartViewArtifactsTypes";
import type { SourceTarget } from "./sourceRequestsState";

const getLabel = (filterLabels: Record<string, Record<string, string>>, key: string, value: string): string => {
  const labels = Object.hasOwn(filterLabels, key) ? filterLabels[key] : undefined;
  return (labels && Object.hasOwn(labels, value) ? labels[value] : undefined) ?? value;
};

const describeOverrides = (
  overrides: Record<string, string[]>,
  filterLabels: Record<string, Record<string, string>>
): string | undefined => {
  const parts = Object.entries(overrides).map(([key, values]) =>
    values.map((value) => getLabel(filterLabels, key, value)).join(", ")
  );
  return parts.length > 0 ? parts.join(" · ") : undefined;
};

interface Props {
  sources: SmartViewArtifactSource[];
  filterLabels: Record<string, Record<string, string>>;
  getTargets: () => SourceTarget[];
  onOpenReport: (target: SourceTarget) => void;
}

const ArtifactDataSources = ({ sources, filterLabels, getTargets, onOpenReport }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [targets, setTargets] = useState<SourceTarget[]>([]);

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    setTargets(getTargets());
    setAnchorEl(event.currentTarget);
  };

  const handleTargetClick = (target: SourceTarget) => {
    setAnchorEl(null);
    onOpenReport(target);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<DatasetOutlinedIcon />}
        endIcon={<ExpandMoreRoundedIcon />}
        onClick={handleButtonClick}
      >
        Data sources
      </Button>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        {targets.length === 0 ? (
          <Typography color="textSecondary" sx={{ p: 2 }}>
            This view shows no data yet.
          </Typography>
        ) : (
          <List>
            {targets.map((target) => (
              <PopoverMenuItem
                key={JSON.stringify([target.sourceKey, target.overrides])}
                onClick={() => handleTargetClick(target)}
              >
                <ListItemText
                  primary={sources.find((source) => source.key === target.sourceKey)?.title ?? target.sourceKey}
                  secondary={describeOverrides(target.overrides, filterLabels)}
                  slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
                />
              </PopoverMenuItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default ArtifactDataSources;
